import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API } from '../config/apiConfig';
import DashboardPageHeader from '../components/DashboardPageHeader';
import { portal, portalGradientTextSx } from '../styles/dashboardBrand';
import {
  EARNINGS_MAX_LEVELS,
  computeEarningsEstimate,
  formatUsd,
  levelCountsArrayFromRows,
} from '../utils/earningsCalculator';

const DISCLAIMER = `This calculator provides illustrative estimates only. Figures are based on the last available MLM period ad prices, your current community downline structure, and assumptions of 6 ads per day at 100% activity. Actual earnings depend on real ad revenue, member activity, eligibility rules, and monthly close processing. This is not a guarantee of future income.`;

function EarningsCalculator() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [periodTab, setPeriodTab] = useState('month');
  const [levelCounts, setLevelCounts] = useState(Array(EARNINGS_MAX_LEVELS).fill(0));
  const [inputs, setInputs] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [saving, setSaving] = useState(false);
  const [levelsEdited, setLevelsEdited] = useState(false);

  const loadData = useCallback(async (uid) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setError(null);
    try {
      const [calcRes, scenRes] = await Promise.all([
        axios.get(API.earningsCalculator(uid), { headers }),
        axios.get(API.earningsCalculatorScenarios(uid), { headers }),
      ]);
      if (!calcRes.data?.success) throw new Error(calcRes.data?.error || 'Failed to load calculator');
      const calc = calcRes.data.calculator;
      setInputs(calc);
      setLevelCounts(levelCountsArrayFromRows(calc.levelCounts));
      setScenarios(scenRes.data?.success ? scenRes.data.scenarios : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load calculator');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    const decoded = jwtDecode(token);
    const uid = decoded.UserID || decoded.userId;
    setUserId(uid);
    loadData(uid);
  }, [loadData]);

  const estimate = useMemo(() => {
    if (!inputs) return null;
    return computeEarningsEstimate({
      levelCounts,
      levelCountsByCountry: inputs.levelCountsByCountry,
      countryPrices: inputs.countryPrices,
      globalAvgGrossPerAdUsd: inputs.globalAvgGrossPerAdUsd,
      userCountryId: inputs.user?.countryOfResidenceId,
      periodTab,
      useUserCountryForAllLevels: levelsEdited,
    });
  }, [inputs, levelCounts, periodTab, levelsEdited]);

  const handleLevelChange = (level, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    setLevelsEdited(true);
    setLevelCounts((prev) => {
      const next = [...prev];
      next[level - 1] = n;
      return next;
    });
  };

  const handleSaveScenario = async () => {
    if (!userId || !scenarioName.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        API.earningsCalculatorScenarios(userId),
        {
          scenarioName: scenarioName.trim(),
          periodTab,
          levelCounts: Object.fromEntries(levelCounts.map((c, i) => [String(i + 1), c])),
        },
        { headers }
      );
      const scenRes = await axios.get(API.earningsCalculatorScenarios(userId), { headers });
      setScenarios(scenRes.data?.success ? scenRes.data.scenarios : []);
      setSaveOpen(false);
      setScenarioName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadScenario = (scenario) => {
    const arr = Array(EARNINGS_MAX_LEVELS).fill(0);
    const src = scenario.levelCounts || {};
    for (let i = 1; i <= EARNINGS_MAX_LEVELS; i += 1) {
      arr[i - 1] = Math.max(0, Number(src[String(i)] ?? src[i]) || 0);
    }
    setLevelCounts(arr);
    setPeriodTab(scenario.periodTab || 'month');
    setLevelsEdited(true);
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!userId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(API.earningsCalculatorScenario(userId, scenarioId), { headers });
      setScenarios((prev) => prev.filter((s) => s.scenarioId !== scenarioId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete scenario');
    }
  };

  const periodLabel = periodTab === 'day' ? 'Daily' : periodTab === 'week' ? 'Weekly' : 'Monthly';

  return (
    <Box>
      <DashboardPageHeader
        title="Earnings Calculator"
        subtitle="Estimate your potential earnings from ads and referrals"
        icon={CalculateIcon}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {inputs?.period?.priceWarning ? (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              {inputs.period.priceWarning}
            </Alert>
          ) : null}

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Price period
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {inputs?.period?.periodStart && inputs?.period?.periodEnd
                ? `${inputs.period.periodStart} → ${inputs.period.periodEnd}`
                : 'No period data'}
              {inputs?.period?.isClosedPeriod ? (
                <Chip label="Closed period" size="small" color="success" sx={{ ml: 1 }} />
              ) : (
                <Chip label="Latest known" size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
            {inputs?.user?.countryName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Your country: {inputs.user.countryName}
              </Typography>
            ) : null}
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Tabs
              value={periodTab}
              onChange={(_, v) => setPeriodTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Daily" value="day" />
              <Tab label="Weekly" value="week" />
              <Tab label="Monthly" value="month" />
            </Tabs>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Own ads ({periodLabel})
                </Typography>
                <Typography variant="h5" sx={portalGradientTextSx({ fontWeight: 800 })}>
                  {formatUsd(estimate?.ownUsd)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Referral bonuses ({periodLabel})
                </Typography>
                <Typography variant="h5" sx={portalGradientTextSx({ fontWeight: 800 })}>
                  {formatUsd(estimate?.referralUsd)}
                </Typography>
                {estimate?.referralCapped ? (
                  <Typography variant="caption" color="warning.main">
                    Capped at {formatUsd(estimate.referralCapUsd)} (raw {formatUsd(estimate.referralRawUsd)})
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Total ({periodLabel})
                </Typography>
                <Typography variant="h4" sx={portalGradientTextSx({ fontWeight: 900 })}>
                  {formatUsd(estimate?.totalUsd)}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Community downline levels (L1–L18)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edit counts to model growth. Hypothetical members use your country ad price.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: 1.5,
              }}
            >
              {Array.from({ length: EARNINGS_MAX_LEVELS }, (_, i) => i + 1).map((level) => (
                <TextField
                  key={level}
                  label={`L${level}`}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                  value={levelCounts[level - 1]}
                  onChange={(e) => handleLevelChange(level, e.target.value)}
                />
              ))}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => setSaveOpen(true)}
                disabled={scenarios.length >= 10}
              >
                Save scenario
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setLevelCounts(levelCountsArrayFromRows(inputs?.levelCounts));
                  setLevelsEdited(false);
                }}
              >
                Reset to my network
              </Button>
            </Stack>
          </Paper>

          {scenarios.length > 0 ? (
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Saved scenarios ({scenarios.length}/10)
              </Typography>
              <Stack spacing={1}>
                {scenarios.map((s) => (
                  <Stack
                    key={s.scenarioId}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Button size="small" onClick={() => handleLoadScenario(s)}>
                      {s.scenarioName}
                    </Button>
                    <Chip label={s.periodTab} size="small" variant="outlined" />
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteScenario(s.scenarioId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ) : null}

          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Disclaimer
            </Typography>
            <Typography variant="body2">{DISCLAIMER}</Typography>
          </Alert>
        </Stack>
      )}

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Scenario name"
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveScenario} variant="contained" disabled={saving || !scenarioName.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EarningsCalculator;
